import { css } from "styled-components";

import { cssColorsFromBackgroundField } from "./background";
import { colorCSS } from "./colors";
import { FONT_FAMILY_SYSTEM, FONT_FAMILY_CODE } from "./constants";
import { redundantCssFromKebab } from "./redundant-css";
import { fontSizes } from "./textSize";

export const normalize: ReturnType<typeof css> = css`
    *,
    *::before,
    *::after {
        ${redundantCssFromKebab("box-sizing", "border-box")}
    }

    body {
        ${cssColorsFromBackgroundField({ color: "N0" })}
        ${redundantCssFromKebab("margin", "0")}
        ${redundantCssFromKebab("font-family", `${FONT_FAMILY_SYSTEM}`)}
        ${redundantCssFromKebab("font-size", `${fontSizes.sm}`)}
        ${redundantCssFromKebab("font-weight", "400")}
        ${redundantCssFromKebab("line-height", "1.5")}
        ${redundantCssFromKebab("text-size-adjust", "100%")}
        ${redundantCssFromKebab(
            "-webkit-tap-highlight-color",
            "rgba(0, 0, 0, 0)"
        )}
    }

    hr {
        ${redundantCssFromKebab("margin", "1rem 0")}
        ${redundantCssFromKebab("color", "inherit")}
        ${redundantCssFromKebab("background-color", "currentColor")}
        ${redundantCssFromKebab("border", "0")}
        ${redundantCssFromKebab("opacity", "0.125")}
    }

    hr:not([size]) {
        ${redundantCssFromKebab("height", "1px")}
    }

    p {
        ${redundantCssFromKebab("margin-top", "0")}
        ${redundantCssFromKebab("margin-bottom", "1rem")}
    }

    abbr[title],
    abbr[data-original-title] {
        ${redundantCssFromKebab("text-decoration", "underline dotted")}
        ${redundantCssFromKebab("cursor", "help")}
        ${redundantCssFromKebab("text-decoration-skip-ink", "none")}
    }

    address {
        ${redundantCssFromKebab("margin-bottom", "1rem")}
        ${redundantCssFromKebab("font-style", "normal")}
        ${redundantCssFromKebab("line-height", "inherit")}
    }

    ol,
    ul {
        ${redundantCssFromKebab("padding-left", "2rem")}
    }

    ol,
    ul,
    dl {
        ${redundantCssFromKebab("margin-top", "0")}
        ${redundantCssFromKebab("margin-bottom", "1rem")}
    }

    ol ol,
    ul ul,
    ol ul,
    ul ol {
        ${redundantCssFromKebab("margin-bottom", "0")}
    }

    dt {
        ${redundantCssFromKebab("font-weight", "700")}
    }

    dd {
        ${redundantCssFromKebab("margin-bottom", "0.5rem")}
        ${redundantCssFromKebab("margin-left", "0")}
    }

    blockquote {
        ${redundantCssFromKebab("margin", "0 0 1rem")}
    }

    b,
    strong {
        ${redundantCssFromKebab("font-weight", "bolder")}
    }

    small {
        ${redundantCssFromKebab("font-size", "0.875em")}
    }

    sub,
    sup {
        ${redundantCssFromKebab("position", "relative")}
        ${redundantCssFromKebab("font-size", "0.75em")}
        ${redundantCssFromKebab("line-height", "0")}
        ${redundantCssFromKebab("vertical-align", "baseline")}
    }

    sub {
        ${redundantCssFromKebab("bottom", "-0.25em")}
    }

    sup {
        ${redundantCssFromKebab("top", "-0.5em")}
    }

    a {
        ${redundantCssFromKebab("color", `${colorCSS("B400")}`)}
    }

    a:hover {
        ${redundantCssFromKebab("color", `${colorCSS("B500")}`)}
    }

    a:not([href]),
    a:not([href]):hover {
        ${redundantCssFromKebab("color", "inherit")}
    }

    pre,
    code,
    kbd,
    samp {
        ${redundantCssFromKebab("font-family", `${FONT_FAMILY_CODE}`)}
        ${redundantCssFromKebab("font-size", "1em")}
    }

    pre {
        ${redundantCssFromKebab("display", "block")}
        ${redundantCssFromKebab("margin-top", "0")}
        ${redundantCssFromKebab("margin-bottom", "1rem")}
        ${redundantCssFromKebab("overflow", "auto")}
        ${redundantCssFromKebab("font-size", "0.875em")}
    }

    pre code {
        ${redundantCssFromKebab("font-size", "inherit")}
        ${redundantCssFromKebab("color", "inherit")}
        ${redundantCssFromKebab("word-break", "normal")}
    }

    code {
        ${redundantCssFromKebab("font-size", "0.875em")}
        ${redundantCssFromKebab("color", `${colorCSS("M500")}`)}
        ${redundantCssFromKebab("word-wrap", "break-word")}
    }

    a > code {
        ${redundantCssFromKebab("color", "inherit")}
    }

    kbd {
        ${redundantCssFromKebab("padding", "0.2rem 0.4rem")}
        ${redundantCssFromKebab("font-size", "0.875em")}
        ${redundantCssFromKebab("color", `${colorCSS("N0")}`)}
        ${redundantCssFromKebab("background-color", `${colorCSS("N900")}`)}
        ${redundantCssFromKebab("border-radius", "0.2rem")}
    }

    kbd kbd {
        ${redundantCssFromKebab("padding", "0")}
        ${redundantCssFromKebab("font-size", "1em")}
        ${redundantCssFromKebab("font-weight", "700")}
    }

    figure {
        ${redundantCssFromKebab("margin", "0 0 1rem")}
    }

    img {
        ${redundantCssFromKebab("vertical-align", "middle")}
    }

    svg {
        ${redundantCssFromKebab("overflow", "hidden")}
        ${redundantCssFromKebab("vertical-align", "middle")}
    }

    table {
        ${redundantCssFromKebab("border-collapse", "collapse")}
    }

    caption {
        ${redundantCssFromKebab("padding-top", "0.5rem")}
        ${redundantCssFromKebab("padding-bottom", "0.5rem")}
        ${redundantCssFromKebab("color", `${colorCSS("N100")}`)}
        ${redundantCssFromKebab("text-align", "left")}
        ${redundantCssFromKebab("caption-side", "bottom")}
    }

    th {
        ${redundantCssFromKebab("text-align", "inherit")}
    }

    label {
        ${redundantCssFromKebab("display", "inline-block")}
        ${redundantCssFromKebab("margin-bottom", "0.25rem")}
    }

    button {
        ${redundantCssFromKebab("border-radius", "0")}
    }

    input,
    button,
    select,
    optgroup,
    textarea {
        ${redundantCssFromKebab("margin", "0")}
        ${redundantCssFromKebab("font-family", "inherit")}
        ${redundantCssFromKebab("font-size", "inherit")}
        ${redundantCssFromKebab("line-height", "inherit")}
    }

    button,
    input {
        ${redundantCssFromKebab("overflow", "visible")}
    }

    button,
    select {
        ${redundantCssFromKebab("text-transform", "none")}
    }

    select {
        ${redundantCssFromKebab("word-wrap", "normal")}
    }

    [list]::-webkit-calendar-picker-indicator {
        ${redundantCssFromKebab("display", "none")}
    }

    button,
    [type="button"],
    [type="reset"],
    [type="submit"] {
        /* stylelint-disable-next-line property-no-vendor-prefix */
        ${redundantCssFromKebab("-webkit-appearance", "button")}
    }

    button:not(:disabled),
    [type="button"]:not(:disabled),
    [type="reset"]:not(:disabled),
    [type="submit"]:not(:disabled) {
        ${redundantCssFromKebab("cursor", "pointer")}
    }

    ::-moz-focus-inner {
        ${redundantCssFromKebab("padding", "0")}
        ${redundantCssFromKebab("border-style", "none")}
    }

    input[type="date"],
    input[type="time"],
    input[type="datetime-local"],
    input[type="month"] {
        /* stylelint-disable-next-line property-no-vendor-prefix */
        ${redundantCssFromKebab("-webkit-appearance", "textfield")}
    }

    textarea {
        ${redundantCssFromKebab("overflow", "auto")}
        ${redundantCssFromKebab("resize", "vertical")}
    }

    fieldset {
        ${redundantCssFromKebab("min-width", "0")}
        ${redundantCssFromKebab("padding", "0")}
        ${redundantCssFromKebab("margin", "0")}
        ${redundantCssFromKebab("border", "0")}
    }

    legend {
        ${redundantCssFromKebab("width", "100%")}
        ${redundantCssFromKebab("padding", "0")}
        ${redundantCssFromKebab("font-size", `${fontSizes.lg}`)}
        ${redundantCssFromKebab("line-height", "inherit")}
        ${redundantCssFromKebab("color", "inherit")}
        ${redundantCssFromKebab("white-space", "normal")}
    }

    mark {
        ${redundantCssFromKebab("padding", "0.2em")}
        ${redundantCssFromKebab("background-color", `${colorCSS("Y50")}`)}
    }

    progress {
        ${redundantCssFromKebab("vertical-align", "baseline")}
    }

    ::-webkit-datetime-edit {
        ${redundantCssFromKebab("overflow", "visible")}
        ${redundantCssFromKebab("line-height", "0")}
    }

    [type="search"] {
        ${redundantCssFromKebab("outline-offset", "-2px")}
        /* stylelint-disable-next-line property-no-vendor-prefix */
        ${redundantCssFromKebab("-webkit-appearance", "textfield")}
    }

    ::-webkit-search-decoration {
        /* stylelint-disable-next-line property-no-vendor-prefix */
        ${redundantCssFromKebab("-webkit-appearance", "none")}
    }

    ::-webkit-color-swatch-wrapper {
        ${redundantCssFromKebab("padding", "0")}
    }

    ::-webkit-file-upload-button {
        ${redundantCssFromKebab("font", "inherit")}
        /* stylelint-disable-next-line property-no-vendor-prefix */
        ${redundantCssFromKebab("-webkit-appearance", "button")}
    }

    output {
        ${redundantCssFromKebab("display", "inline-block")}
    }

    summary {
        ${redundantCssFromKebab("display", "list-item")}
        ${redundantCssFromKebab("cursor", "pointer")}
    }

    template {
        ${redundantCssFromKebab("display", "none")}
    }

    main {
        ${redundantCssFromKebab("display", "block")}
    }

    [hidden] {
        ${redundantCssFromKebab("display", "none !important")}
    }
`;
